import { useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import type { SortOption, WorkerTerminalError } from '../types/worker'

export interface ReservationQueueLineItem {
	item_id: string
	product_id: string
	sku: string | null
	name: string | null
	image_url: string | null
	reference_code: string | null
	color: string
	size: string
	quantity: number
	source_location_id: string | null
	available: number
}

export interface ReservationQueueItem {
	id: string
	reservation_number: string
	customer_name: string
	customer_email: string | null
	customer_phone: string | null
	customer_address: string | null
	status: string
	notes: string | null
	total_amount: number
	reserved_at: string | null
	expires_at: string | null
	created_at: string
	source: string
	source_reference: string | null
	is_store_pickup: boolean
	shopify_order_id: number | null
	source_order_number: string | null
	items: ReservationQueueLineItem[]
	item_count: number
	unit_count: number
}

interface UseReservationQueueConfig {
	adminId: string | null
	pageSize?: number
	sortBy?: SortOption
}

interface UseReservationQueueReturn {
	reservations: ReservationQueueItem[]
	loading: boolean
	error: WorkerTerminalError | null
	totalCount: number
	currentPage: number
	hasMore: boolean
	loadReservations: (page?: number) => Promise<void>
	loadMore: () => Promise<void>
	refresh: () => Promise<void>
	clearError: () => void
}

function parseError(err: unknown): WorkerTerminalError {
	if (err instanceof Error) {
		const message = err.message.toLowerCase()
		if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
			return { type: 'connection', message: 'Unable to connect. Please check your internet connection.', retryable: true }
		}
		if (message.includes('auth') || message.includes('jwt') || message.includes('token')) {
			return { type: 'auth', message: 'Your session has expired. Please log in again.', retryable: false }
		}
	}
	return { type: 'data', message: 'Unable to load reservations. Please try again.', retryable: true }
}

export function useReservationQueue({
	adminId,
	pageSize = 25,
	sortBy = 'oldest',
}: UseReservationQueueConfig): UseReservationQueueReturn {
	const [reservations, setReservations] = useState<ReservationQueueItem[]>([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<WorkerTerminalError | null>(null)
	const [totalCount, setTotalCount] = useState(0)
	const [currentPage, setCurrentPage] = useState(0)

	const loadReservations = useCallback(
		async (page: number = 0) => {
			if (!adminId) return

			setLoading(true)
			setError(null)

			try {
				const offset = page * pageSize

				const { data, error: rpcError } = await supabase.rpc('get_reservations_to_pick', {
					p_admin_id: adminId,
					p_limit: pageSize,
					p_offset: offset,
					p_sort_by: sortBy,
				})

				if (rpcError) throw rpcError

				if (data && data.length > 0) {
					const mapped: ReservationQueueItem[] = data.map((row: any) => ({
						id: row.id,
						reservation_number: row.reservation_number,
						customer_name: row.customer_name,
						customer_email: row.customer_email,
						customer_phone: row.customer_phone,
						customer_address: row.customer_address,
						status: row.status,
						notes: row.notes,
						total_amount: row.total_amount,
						reserved_at: row.reserved_at,
						expires_at: row.expires_at,
						created_at: row.created_at,
						source: row.source,
						source_reference: row.source_reference,
						is_store_pickup: Boolean(row.is_store_pickup),
						shopify_order_id: row.shopify_order_id != null ? Number(row.shopify_order_id) : null,
						source_order_number: row.source_order_number,
						items: Array.isArray(row.items) ? row.items : [],
						item_count: row.item_count ?? 0,
						unit_count: row.unit_count ?? 0,
					}))

					if (page === 0) setReservations(mapped)
					else setReservations((prev) => [...prev, ...mapped])

					setTotalCount(Number(data[0].total_count) || 0)
					setCurrentPage(page)
				} else if (page === 0) {
					setReservations([])
					setTotalCount(0)
				}
			} catch (err) {
				console.error('Failed to load reservations:', err)
				setError(parseError(err))
			} finally {
				setLoading(false)
			}
		},
		[adminId, pageSize, sortBy]
	)

	const loadMore = useCallback(async () => {
		const nextPage = currentPage + 1
		const loadedCount = (currentPage + 1) * pageSize
		if (loadedCount >= totalCount) return
		await loadReservations(nextPage)
	}, [currentPage, pageSize, totalCount, loadReservations])

	const refresh = useCallback(async () => {
		setReservations([])
		setCurrentPage(0)
		await loadReservations(0)
	}, [loadReservations])

	const clearError = useCallback(() => setError(null), [])

	const hasMore = reservations.length < totalCount

	return {
		reservations,
		loading,
		error,
		totalCount,
		currentPage,
		hasMore,
		loadReservations,
		loadMore,
		refresh,
		clearError,
	}
}
